import React, { useEffect, useState, useRef } from "react";
import './horizonTable.css';
import { toast } from "react-toastify";
import SignatureCanvas from "react-signature-canvas";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import onsiteproLogo from '../../assets/logo2 2.png';
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { ensureFormUuid, noopCreateSuccess } from "../../utils/formUuid";
import { buildSharePointFileName, toDdMmYyyy, addedByName } from "../../utils/formPdfHelpers";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { canEditDeleteRecords } from "../../utils/rolePermissions";
import {
  ADMIN_THEME,
  adminEditLabelStyle,
  adminEditInputStyle,
  parseSiteRegisterDate,
  formatSiteRegisterDate,
  siteRegisterDatePickerProps,
  adminEditModalOverlayStyle,
  adminEditModalContentStyle,
  adminEditModalHeaderStyle,
  adminEditModalTitleStyle,
  adminEditModalBodyStyle,
  adminEditModalFooterStyle,
  adminEditCancelBtnStyle,
  adminEditSaveBtnStyle,
  adminEditSigBoxStyle,
} from "./adminEditModalTheme";

function AsbestosBriefingPage() {
  const canEditDelete = canEditDeleteRecords();
  const [appUsers, setAppUsers] = useState([]);
  const [asbestosBriefings, setAsbestosBriefings] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 4;

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [showGroupMemberDeleteModal, setShowGroupMemberDeleteModal] = useState(false);
  const [groupMemberToDelete, setGroupMemberToDelete] = useState(null);
  const [editBriefing, setEditBriefing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const [newBriefing, setNewBriefing] = useState({
    formUuid: '',
    locationOfInduction: '',
    office: '',
    briefingConductedBY: '',
    date: '',
    signature: ''
  });

  // Group entries state for dynamic rows
  const [groupEntries, setGroupEntries] = useState([
    {
      surname: '',
      firstName: '',
      jobTitleDepartment: '',
      placeOfWork: '',
      subject: '',
      date: '',
      signature: ''
    }
  ]);

  const [editSignature, setEditSignature] = useState("");
  const [editGroupEntries, setEditGroupEntries] = useState([]);
  
  const sigCanvasRef = useRef(null);
  const editSigCanvasRef = useRef(null);
  
  // Create refs for group entries - we'll manage these separately
  const groupEntryRefs = useRef({});
  const editGroupEntryRefs = useRef({});

  const [uploadingKey, setUploadingKey] = useState(null);
  
  // SharePoint upload modal state
  const [showSharePointUploadModal, setShowSharePointUploadModal] = useState(false);
  const [sharePointUploadRecord, setSharePointUploadRecord] = useState(null);
  const [sharePointPhaseType, setSharePointPhaseType] = useState('phase');
  const [sharePointPhaseNumber, setSharePointPhaseNumber] = useState('15');
  const [sharePointNetwork, setSharePointNetwork] = useState('');
  const [pendingDirectUpload, setPendingDirectUpload] = useState(false);
  useEffect(() => {
    if (pendingDirectUpload && sharePointUploadRecord) {
      setPendingDirectUpload(false);
      // eslint-disable-next-line no-use-before-define
      handleSharePointUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDirectUpload, sharePointUploadRecord]);

  // Group entries management functions
  const addGroupEntry = () => {
    setGroupEntries([...groupEntries, {
      surname: '',
      firstName: '',
      jobTitleDepartment: '',
      placeOfWork: '',
      subject: '',
      date: '',
      signature: ''
    }]);
  };

  const removeGroupEntry = (index) => {
    if (groupEntries.length > 1) {
      const entry = groupEntries[index];
      const memberName = entry.firstName || entry.surname 
        ? `${entry.firstName || ''} ${entry.surname || ''}`.trim() 
        : `Group Member ${index + 1}`;
      setGroupEntries(groupEntries.filter((_, i) => i !== index));
      toast.success(`${memberName || 'Group member'} removed successfully!`);
    } else {
      toast.warning("At least one group member is required!");
    }
  };

  const updateGroupEntry = (index, field, value) => {
    setGroupEntries(groupEntries.map((entry, i) => 
      i === index ? { ...entry, [field]: value } : entry
    ));
  };

  const clearGroupSignature = (index) => {
    if (groupEntryRefs.current[index]) {
      groupEntryRefs.current[index].clear();
    }
    updateGroupEntry(index, 'signature', '');
  };

  const saveGroupSignature = (index) => {
    if (groupEntryRefs.current[index] && !groupEntryRefs.current[index].isEmpty()) {
      const signature = groupEntryRefs.current[index].getTrimmedCanvas().toDataURL("image/png");
      updateGroupEntry(index, 'signature', signature);
    }
  };

  // Edit group entries management functions
  const addEditGroupEntry = () => {
    setEditGroupEntries([...editGroupEntries, {
      surname: '',
      firstName: '',
      jobTitleDepartment: '',
      placeOfWork: '',
      subject: '',
      date: '',
      signature: ''
    }]);
  };

  const removeEditGroupEntry = (index) => {
    if (editGroupEntries.length > 1) {
      setGroupMemberToDelete(index);
      setShowGroupMemberDeleteModal(true);
    } else {
      toast.warning("At least one group member is required!");
    }
  };

  const confirmRemoveEditGroupEntry = () => {
    if (groupMemberToDelete !== null && editGroupEntries.length > 1) {
      const entry = editGroupEntries[groupMemberToDelete];
      const memberName = entry.firstName || entry.surname 
        ? `${entry.firstName || ''} ${entry.surname || ''}`.trim() 
        : `Group Member ${groupMemberToDelete + 1}`;
      setEditGroupEntries(editGroupEntries.filter((_, i) => i !== groupMemberToDelete));
      toast.success(`${memberName || 'Group member'} removed successfully!`);
      setShowGroupMemberDeleteModal(false);
      setGroupMemberToDelete(null);
    }
  };

  const updateEditGroupEntry = (index, field, value) => {
    setEditGroupEntries(editGroupEntries.map((entry, i) => 
      i === index ? { ...entry, [field]: value } : entry
    ));
  };

  const clearEditGroupSignature = (index) => {
    if (editGroupEntryRefs.current[index]) {
      editGroupEntryRefs.current[index].clear();
    }
    updateEditGroupEntry(index, 'signature', '');
  };

  const saveEditGroupSignature = (index) => {
    // Don't update state on every draw - keep canvas editable
    // Signature will be captured when Save is clicked
    // This function is kept for potential future use but won't auto-save
  };

  // Signature pad handlers for Add Modal - Main Signature
  const clearSignature = () => {
    sigCanvasRef.current.clear();
    setNewBriefing(prev => ({ ...prev, signature: "" }));
  };
  const saveSignature = () => {
    if (!sigCanvasRef.current.isEmpty()) {
      setNewBriefing(prev => ({
        ...prev,
        signature: sigCanvasRef.current.getTrimmedCanvas().toDataURL("image/png")
      }));
    }
  };

  // Signature pad handlers for Edit Modal - Main Signature
  const clearEditSignature = () => {
    if (editSigCanvasRef.current) {
      editSigCanvasRef.current.clear();
    }
    setEditSignature("");
    setEditBriefing(prev => ({ ...prev, signature: "" }));
  };
  const saveEditSignature = () => {
    // Don't update state on every draw - keep canvas editable
    // Signature will be captured when Save is clicked
    // This function is kept for potential future use but won't auto-save
  };

  useEffect(() => {
    async function fetchAsbestosBriefings() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(baseUrl + "/api/asbestos-briefing", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.success && Array.isArray(data.data)) {
          setAsbestosBriefings(data.data);
        } else {
          setAsbestosBriefings([]);
        }
      } catch {
        setAsbestosBriefings([]);
      }
    }
    async function fetchAppUsers() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(baseUrl + "/api/app-users", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.success && Array.isArray(data.data)) {
          setAppUsers(data.data);
        } else {
          setAppUsers([]);
        }
      } catch {
        setAppUsers([]);
      }
    }
    fetchAsbestosBriefings();
    fetchAppUsers();
  }, []);


  // Improved search: split search string into words, ignore extra spaces, match if any word is present in any field
  const filtered = asbestosBriefings.filter(r => {
    // Handle both string and array fields
    const getSearchableText = (val) => {
      if (!val) return '';
      if (Array.isArray(val)) {
        return val.join(' ').toLowerCase();
      }
      return String(val).toLowerCase();
    };
    
    const fields = [
      r.locationOfInduction, 
      r.office, 
      r.surnameGroup, 
      r.firstNameGroup, 
      r.briefingConductedBY
    ].map(val => getSearchableText(val));
    
    const searchWords = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (searchWords.length === 0) return true;
    return searchWords.every(word => fields.some(field => field.includes(word)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice().slice((page-1)*pageSize, page*pageSize);
  
  // Reset page to 1 if current page is beyond available pages
  if (filtered.length === 0 && page > 1) {
    setPage(1);
  }

  // Add Asbestos Briefing
  const handleAdd = async () => {
    // Basic validation
    if (!newBriefing.locationOfInduction || !newBriefing.office || !newBriefing.briefingConductedBY) {
      toast.error("Location of Induction, Office, and Briefing Conducted by are required");
      return;
    }
    
    // Validate group entries
    const hasValidGroupEntry = groupEntries.some(entry => 
      entry.surname && entry.firstName
    );
    
    if (!hasValidGroupEntry) {
      toast.error("At least one group entry with Surname and First Name is required");
      return;
    }

    // Collect group data into arrays
    // Get user details for saving firstName and lastName if userId is available
    let addedByFirstName = '';
    let addedByLastName = '';
    if (newBriefing.userId) {
      const user = appUsers.find(u => u._id === newBriefing.userId);
      if (user) {
        addedByFirstName = user.firstName || '';
        addedByLastName = user.lastName || '';
      }
    }
    
    const briefingData = {
      ...newBriefing,
      formUuid: ensureFormUuid(newBriefing.formUuid),
      addedByFirstName,
      addedByLastName,
      surnameGroup: groupEntries.map(entry => entry.surname),
      firstNameGroup: groupEntries.map(entry => entry.firstName),
      jobTitleDepartmentGroup: groupEntries.map(entry => entry.jobTitleDepartment),
      placeOfWorkGroup: groupEntries.map(entry => entry.placeOfWork),
      subjectGroup: groupEntries.map(entry => entry.subject),
      dateGroup: groupEntries.map(entry => entry.date),
      signatureGroup: groupEntries.map(entry => entry.signature)
    };

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + "/api/asbestos-briefing", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(briefingData)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (noopCreateSuccess(data)) {
          toast.warn(data.message || "This record already exists. No new record was added.");
          return;
        }
        setAsbestosBriefings([...asbestosBriefings, data.data]);
        setShowAddModal(false);
        setNewBriefing({
          formUuid: '',
          locationOfInduction: '',
          office: '',
          briefingConductedBY: '',
          date: '',
          signature: ''
        });
        setGroupEntries([{
          surname: '',
          firstName: '',
          jobTitleDepartment: '',
          placeOfWork: '',
          subject: '',
          date: '',
          signature: ''
        }]);
        toast.success("Asbestos briefing added!");
      } else {
        toast.error(data.message || "Failed to add");
      }
    } catch {
      toast.error("Failed to add");
    }
  };

  // Edit Asbestos Briefing
  const handleEdit = (briefing) => {
    setEditBriefing({ ...briefing, formUuid: ensureFormUuid(briefing?.formUuid) });
    setEditSignature(briefing.signature || "");
    
    // Parse group data and populate edit group entries
    let surnameGroup = briefing.surnameGroup || [];
    let firstNameGroup = briefing.firstNameGroup || [];
    let jobTitleDepartmentGroup = briefing.jobTitleDepartmentGroup || [];
    let placeOfWorkGroup = briefing.placeOfWorkGroup || [];
    let subjectGroup = briefing.subjectGroup || [];
    let dateGroup = briefing.dateGroup || [];
    let signatureGroup = briefing.signatureGroup || [];
    
    // Parse if stored as JSON strings
    if (typeof surnameGroup === 'string' && surnameGroup.startsWith('[')) {
      try {
        surnameGroup = JSON.parse(surnameGroup);
        firstNameGroup = JSON.parse(firstNameGroup);
        jobTitleDepartmentGroup = JSON.parse(jobTitleDepartmentGroup);
        placeOfWorkGroup = JSON.parse(placeOfWorkGroup);
        subjectGroup = JSON.parse(subjectGroup);
        dateGroup = JSON.parse(dateGroup);
        signatureGroup = JSON.parse(signatureGroup);
      } catch (e) {
        surnameGroup = [surnameGroup];
        firstNameGroup = [firstNameGroup];
        jobTitleDepartmentGroup = [jobTitleDepartmentGroup];
        placeOfWorkGroup = [placeOfWorkGroup];
        subjectGroup = [subjectGroup];
        dateGroup = [dateGroup];
        signatureGroup = [signatureGroup];
      }
    }
    
    // Ensure arrays
    if (!Array.isArray(surnameGroup)) surnameGroup = [surnameGroup];
    if (!Array.isArray(firstNameGroup)) firstNameGroup = [firstNameGroup];
    if (!Array.isArray(jobTitleDepartmentGroup)) jobTitleDepartmentGroup = [jobTitleDepartmentGroup];
    if (!Array.isArray(placeOfWorkGroup)) placeOfWorkGroup = [placeOfWorkGroup];
    if (!Array.isArray(subjectGroup)) subjectGroup = [subjectGroup];
    if (!Array.isArray(dateGroup)) dateGroup = [dateGroup];
    if (!Array.isArray(signatureGroup)) signatureGroup = [signatureGroup];
    
    // Create edit group entries
    const entries = surnameGroup.map((surname, index) => ({
      surname: surname || '',
      firstName: firstNameGroup[index] || '',
      jobTitleDepartment: jobTitleDepartmentGroup[index] || '',
      placeOfWork: placeOfWorkGroup[index] || '',
      subject: subjectGroup[index] || '',
      date: dateGroup[index] || '',
      signature: signatureGroup[index] || ''
    }));
    
    setEditGroupEntries(entries.length > 0 ? entries : [{
      surname: '',
      firstName: '',
      jobTitleDepartment: '',
      placeOfWork: '',
      subject: '',
      date: '',
      signature: ''
    }]);
    
    setShowEditModal(true);
    setTimeout(() => {
      if (editSigCanvasRef.current) {
        editSigCanvasRef.current.clear();
        if (briefing.signature) {
          let sigSrc = briefing.signature;
          if (!sigSrc.startsWith('data:image')) {
            sigSrc = 'data:image/png;base64,' + sigSrc;
          }
          const img = new window.Image();
          img.onload = () => {
            if (editSigCanvasRef.current) {
              const canvas = editSigCanvasRef.current.getCanvas();
              const ctx = canvas.getContext('2d');
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              const canvasAspect = canvas.width / canvas.height;
              const imgAspect = img.width / img.height;
              
              let drawWidth, drawHeight, drawX, drawY;
              
              if (imgAspect > canvasAspect) {
                drawWidth = canvas.width;
                drawHeight = canvas.width / imgAspect;
                drawX = 0;
                drawY = (canvas.height - drawHeight) / 2;
              } else {
                drawHeight = canvas.height;
                drawWidth = canvas.height * imgAspect;
                drawX = (canvas.width - drawWidth) / 2;
                drawY = 0;
              }
              
              ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
            }
          };
          img.onerror = () => {
            console.error('Failed to load signature image');
          };
          img.src = sigSrc;
        }
       }
     }, 300);
     
     // Load group signatures using Site Register method
     setTimeout(() => {
       entries.forEach((entry, index) => {
         if (editGroupEntryRefs.current[index] && entry.signature) {
           let sigSrc = entry.signature;
           // If it's just base64, prepend the header
           if (!sigSrc.startsWith('data:image')) {
             sigSrc = 'data:image/png;base64,' + sigSrc;
           }
           const img = new window.Image();
           img.onload = () => {
             if (editGroupEntryRefs.current[index]) {
               const canvas = editGroupEntryRefs.current[index].getCanvas();
               const ctx = canvas.getContext('2d');
               ctx.clearRect(0, 0, canvas.width, canvas.height);
               // Draw the image with proper scaling to maintain aspect ratio
               const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
               const x = (canvas.width - img.width * scale) / 2;
               const y = (canvas.height - img.height * scale) / 2;
               ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
             }
           };
           img.onerror = () => {
             console.error('Failed to load group signature image');
           };
           img.src = sigSrc;
         }
       });
     }, 400);
   };

  const handleEditSave = async () => {
    if (!editBriefing.locationOfInduction || !editBriefing.office || !editBriefing.briefingConductedBY) {
      toast.error("Location of Induction, Office, and Briefing Conducted by are required");
      return;
    }
    
    // Validate edit group entries
    const hasValidGroupEntry = editGroupEntries.some(entry => 
      entry.surname && entry.firstName
    );
    
    if (!hasValidGroupEntry) {
      toast.error("At least one group entry with Surname and First Name is required");
      return;
    }

    // Capture latest signature from canvas before saving (in case user drew but onEnd didn't fire)
    let finalSignature = editBriefing.signature;
    if (editSigCanvasRef.current && !editSigCanvasRef.current.isEmpty()) {
      const canvas = editSigCanvasRef.current.getCanvas();
      finalSignature = canvas.toDataURL("image/png");
    }

    // Capture latest group signatures from canvas before saving
    const finalGroupEntries = editGroupEntries.map((entry, index) => {
      if (editGroupEntryRefs.current[index] && !editGroupEntryRefs.current[index].isEmpty()) {
        const canvas = editGroupEntryRefs.current[index].getCanvas();
        return { ...entry, signature: canvas.toDataURL("image/png") };
      }
      return entry;
    });

    // Collect group data into arrays
    // Get user details for saving firstName and lastName if userId is available
    let addedByFirstName = editBriefing.addedByFirstName || '';
    let addedByLastName = editBriefing.addedByLastName || '';
    if (editBriefing.userId) {
      const user = appUsers.find(u => u._id === editBriefing.userId);
      if (user) {
        addedByFirstName = user.firstName || '';
        addedByLastName = user.lastName || '';
      }
    }
    
    const briefingData = {
      ...editBriefing,
      formUuid: ensureFormUuid(editBriefing.formUuid),
      addedByFirstName,
      addedByLastName,
      signature: finalSignature,
      surnameGroup: finalGroupEntries.map(entry => entry.surname),
      firstNameGroup: finalGroupEntries.map(entry => entry.firstName),
      jobTitleDepartmentGroup: finalGroupEntries.map(entry => entry.jobTitleDepartment),
      placeOfWorkGroup: finalGroupEntries.map(entry => entry.placeOfWork),
      subjectGroup: finalGroupEntries.map(entry => entry.subject),
      dateGroup: finalGroupEntries.map(entry => entry.date),
      signatureGroup: finalGroupEntries.map(entry => entry.signature)
    };

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + `/api/asbestos-briefing/${editBriefing._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(briefingData)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setAsbestosBriefings(asbestosBriefings.map(r => r._id === editBriefing._id ? { ...briefingData } : r));
        setShowEditModal(false);
        toast.success("Asbestos briefing updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  // Delete Asbestos Briefing
  // SharePoint Upload Handler
  const handleSharePointUpload = async () => {
    const locationForUpload = (sharePointUploadRecord?.locationOfInduction || '').trim();

    if (!locationForUpload) {
      toast.error('Location of Induction is missing for this record.');
      return;
    }

    if (sharePointPhaseType === 'phase' && (!sharePointNetwork || sharePointNetwork.trim() === '')) {
      toast.error('Please select an area (EPN, LPN, or SPN)');
      return;
    }

    const payloadPhaseType = sharePointPhaseType === 'phase' ? 'phase' : 'non-phase';
    const payloadNetwork = sharePointPhaseType === 'phase' ? sharePointNetwork : '';
    const payloadPhaseNumber = sharePointPhaseType === 'phase' ? sharePointPhaseNumber : '';

    setShowSharePointUploadModal(false);

    await generatePDF(sharePointUploadRecord, {
      uploadOnly: true,
      phaseType: payloadPhaseType,
      phaseNumber: payloadPhaseNumber,
      network: payloadNetwork,
      siteName: locationForUpload,
      onSuccess: () => {
        setSharePointUploadRecord(null);
        setSharePointPhaseType('phase');
        setSharePointPhaseNumber('15');
        setSharePointNetwork('');
      },
      onError: () => {}
    });
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };
  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + `/api/asbestos-briefing/${deleteId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setAsbestosBriefings(asbestosBriefings.filter(r => r._id !== deleteId));
        toast.success("Deleted successfully!");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const generatePDF = async (record, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint ? {
      phaseType: options.phaseType || 'non-phase',
      phaseNumber: options.phaseNumber || '',
      network: options.network || '',
      siteName: options.siteName || '',
      onSuccess: options.onSuccess,
      onError: options.onError
    } : null;
    
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Modern color scheme
      const primaryColor = [139, 22, 52]; // #8b1634
      
      // Helper function to draw modern rounded rectangle
      const drawRoundedRect = (x, y, width, height, radius, fillColor, strokeColor, hasShadow = false) => {
        if (hasShadow) {
          doc.setFillColor(0, 0, 0, 0.1);
          doc.roundedRect(x + 1, y + 1, width, height, radius, radius, 'F');
        }
        doc.setFillColor(...fillColor);
        doc.setDrawColor(...strokeColor);
        doc.setLineWidth(0.5);
        doc.roundedRect(x, y, width, height, radius, radius, 'FD');
      };
      
      // ---- HEADER SECTION ----
      const headerHeight = 30;
      drawRoundedRect(0, 0, pageWidth, headerHeight, 0, primaryColor, primaryColor);
      
      // ---- ADD HIGH-RES LOGO ----
      const addLogoToPDF = () => {
        return new Promise((resolve) => {
          const logoImg = new Image();
          logoImg.crossOrigin = 'anonymous';
          logoImg.onload = function () {
            try {
              const logoWidth = 70;
              const aspectRatio = logoImg.height / logoImg.width;
              const logoHeight = logoWidth * aspectRatio;
              const logoX = 10;
              const logoY = (headerHeight - logoHeight) / 2;
              
              const canvas = document.createElement('canvas');
              const scale = 2.5;
              canvas.width = logoImg.width * scale;
              canvas.height = logoImg.height * scale;
              const ctx = canvas.getContext('2d');
              ctx.scale(scale, scale);
              ctx.drawImage(logoImg, 0, 0);
              
              const highResDataUrl = canvas.toDataURL('image/png', 1.0);
              doc.addImage(highResDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight, '', 'NONE');
            } catch (error) {
              doc.setFillColor(255, 255, 255);
              doc.rect(10, 6, 60, 15, 'F');
              doc.setFont("helvetica", "bold");
              doc.setFontSize(10);
              doc.setTextColor(0, 0, 0);
              doc.text("TAYLOR TECHNOLOGY", 15, 12);
              doc.text("SYSTEMS LIMITED", 15, 17);
            }
            resolve();
          };
          logoImg.onerror = function () {
            doc.setFillColor(255, 255, 255);
            doc.rect(10, 6, 60, 15, 'F');
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            doc.text("TAYLOR TECHNOLOGY", 15, 12);
            doc.text("SYSTEMS LIMITED", 15, 17);
            resolve();
          };
          logoImg.src = onsiteproLogo;
        });
      };
      
      await addLogoToPDF();
      
      // ---- HEADER TEXT ----
      const titleX = 90;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text("Asbestos Briefing Report", titleX, 13);
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", titleX, 19);
      
      let yPosition = headerHeight + 10;
      
      // ---- Section 1: Location and Office Details ----
      doc.setFillColor(139, 22, 52);
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      
      doc.rect(10, yPosition, pageWidth - 20, 8, "F");
      doc.text("1", 14, yPosition + 5.5);
      doc.text("Location and Office Details", 22, yPosition + 5.5);
      yPosition += 8;
      
      autoTable(doc, {
        startY: yPosition,
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 130 },
        },
        head: [
          [
            { content: "Location of Induction:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            { content: record.locationOfInduction || "", styles: { halign: "center" } },
          ],
          [
            { content: "Office:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            { content: record.office || "", styles: { halign: "center" } },
          ],
        ],
        body: [],
        styles: {
          font: "helvetica",
          fontSize: 9,
          cellPadding: 3,
          overflow: "linebreak",
          valign: "middle",
        },
        theme: "grid",
        tableWidth: pageWidth - 20,
        margin: { left: 10 },
        headStyles: {
          fillColor: [245, 245, 245],
          textColor: [0, 0, 0],
          lineWidth: 0.2,
        },
        bodyStyles: {
          lineWidth: 0.2,
          textColor: [0, 0, 0],
        },
      });
      
      yPosition = doc.lastAutoTable.finalY + 2;
      
      // Helper function to add signature with proper sizing
      const addSignatureToPDF = async (signatureData, x, y, maxWidth, maxHeight) => {
          if (!signatureData) return;
          
          try {
            let sigSrc = signatureData;
            if (!sigSrc.startsWith('data:image')) {
              sigSrc = 'data:image/png;base64,' + sigSrc;
            }
            
            return new Promise((resolve) => {
              const sigImg = new Image();
              sigImg.crossOrigin = 'anonymous';
              
              sigImg.onload = () => {
                try {
                  const aspectRatio = sigImg.width / sigImg.height;
                  let imgWidth = maxWidth;
                  let imgHeight = maxWidth / aspectRatio;
                  
                  if (imgHeight > maxHeight) {
                    imgHeight = maxHeight;
                    imgWidth = maxHeight * aspectRatio;
                  }
                  
                  imgWidth = Math.min(imgWidth, maxWidth);
                  imgHeight = Math.min(imgHeight, maxHeight);
                  
                  const scale = 4;
                  const canvas = document.createElement('canvas');
                  canvas.width = Math.round(imgWidth * scale);
                  canvas.height = Math.round(imgHeight * scale);
                  const ctx = canvas.getContext('2d');
                  ctx.imageSmoothingEnabled = true;
                  ctx.imageSmoothingQuality = 'high';
                  ctx.fillStyle = '#FFFFFF';
                  ctx.fillRect(0, 0, canvas.width, canvas.height);
                  ctx.drawImage(sigImg, 0, 0, sigImg.width, sigImg.height, 0, 0, canvas.width, canvas.height);
                  const highResDataUrl = canvas.toDataURL('image/png', 1.0);
                  doc.addImage(highResDataUrl, 'PNG', x, y, imgWidth, imgHeight, '', 'NONE');
                } catch (err) {
                  console.error('Error adding signature to PDF:', err);
                  try {
                    doc.addImage(sigSrc, 'PNG', x, y, maxWidth, maxHeight);
                  } catch (fallbackErr) {
                    console.error('Fallback signature addition also failed:', fallbackErr);
                  }
                }
                resolve();
              };
              
              sigImg.onerror = () => {
                console.error('Error loading signature image');
                resolve();
              };
              
              sigImg.src = sigSrc;
            });
          } catch (err) {
            console.error('Signature processing error:', err);
          }
        };
      
      // ---- Section 2: Group Details ----
      // Parse group data
      const parseField = (field) => {
        if (Array.isArray(field)) {
          if (field.length === 1 && typeof field[0] === 'string' && field[0].startsWith('[') && field[0].endsWith(']')) {
            let inner = field[0];
            try {
              const parsed = JSON.parse(inner);
              if (Array.isArray(parsed)) return parsed;
            } catch {
              return inner.slice(1, -1).split(',').map(s => s.trim().replace(/^"|"$/g, ''));
            }
          }
          return field;
        }
        if (typeof field === 'string' && field.startsWith('[') && field.endsWith(']')) {
          let inner = field;
          try {
            const parsed = JSON.parse(inner);
            if (Array.isArray(parsed)) return parsed;
          } catch {
            return inner.slice(1, -1).split(',').map(s => s.trim().replace(/^"|"$/g, ''));
          }
        }
        return field ? [field] : [];
      };
      
      let surnames = parseField(record.surnameGroup || []);
      let firstNames = parseField(record.firstNameGroup || []);
      let jobTitles = parseField(record.jobTitleDepartmentGroup || []);
      let placesOfWork = parseField(record.placeOfWorkGroup || []);
      let subjects = parseField(record.subjectGroup || []);
      let dates = parseField(record.dateGroup || []);
      let signatures = [];
      
      // Parse signatureGroup
      if (record.signatureGroup) {
        try {
          if (typeof record.signatureGroup === 'string' && record.signatureGroup.startsWith('[') && record.signatureGroup.endsWith(']')) {
            signatures = JSON.parse(record.signatureGroup);
            if (!Array.isArray(signatures)) {
              signatures = [signatures];
            }
          } else if (Array.isArray(record.signatureGroup)) {
            signatures = record.signatureGroup;
          } else if (record.signatureGroup.includes(',')) {
            signatures = record.signatureGroup.split(',').map(s => {
              let cleaned = s.trim();
              if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
                cleaned = cleaned.slice(1, -1);
              } else if (cleaned.startsWith("'") && cleaned.endsWith("'")) {
                cleaned = cleaned.slice(1, -1);
              }
              return cleaned;
            }).filter(Boolean);
          } else {
            let cleaned = record.signatureGroup.trim();
            if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
              cleaned = cleaned.slice(1, -1);
            } else if (cleaned.startsWith("'") && cleaned.endsWith("'")) {
              cleaned = cleaned.slice(1, -1);
            }
            signatures = [cleaned];
          }
        } catch (e) {
          console.error('Error parsing signatureGroup:', e);
          signatures = [];
        }
      }
      
      // Create blocks from parsed data
      const blocks = [];
      const maxLength = Math.max(surnames.length, firstNames.length, jobTitles.length, placesOfWork.length, subjects.length, dates.length, signatures.length);
      
      for (let i = 0; i < maxLength; i++) {
        const surname = surnames[i] || "";
        const firstName = firstNames[i] || "";
        const jobTitle = jobTitles[i] || "";
        const placeOfWork = placesOfWork[i] || "";
        const subject = subjects[i] || "";
        const date = dates[i] || "";
        const signature = signatures[i] || "";
        
        if (surname.trim() || firstName.trim() || jobTitle.trim() || placeOfWork.trim() || subject.trim() || date.trim() || signature.trim()) {
          blocks.push({ surname, firstName, jobTitle, placeOfWork, subject, date, signature });
        }
      }
      
      // Display group details section
      if (blocks.length > 0) {
        if (yPosition + 60 > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFillColor(139, 22, 52);
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.rect(10, yPosition, pageWidth - 20, 8, "F");
        doc.text("2", 14, yPosition + 5.5);
        doc.text("Group Details", 22, yPosition + 5.5);
        yPosition += 8;
        
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        doc.text("We the undersigned confirm that we have been briefed and understand the document(s) as detailed above", 15, yPosition + 5);
        yPosition += 10;
        
        for (let i = 0; i < blocks.length; i++) {
          const block = blocks[i];
          
          // Check if we need a new page
          if (yPosition + 70 > pageHeight - 30) {
            doc.addPage();
            yPosition = 20;
          }
          
          // Signature box
          const boxWidth = pageWidth - 20;
          const boxHeight = 60;
          
          doc.setDrawColor(200, 200, 200);
          doc.setFillColor(248, 248, 248);
          doc.roundedRect(10, yPosition, boxWidth, boxHeight, 3, 3, "FD");
          
          doc.setTextColor(139, 22, 52);
          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          doc.text(`Group Member ${i + 1}`, 15, yPosition + 8);
          
          // Group member details
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(0, 0, 0);
          const surnameText = block.surname ? `Surname: ${block.surname}` : "Surname: -";
          const firstNameText = block.firstName ? `First Name: ${block.firstName}` : "First Name: -";
          const jobTitleText = block.jobTitle ? `Job Title & Department: ${block.jobTitle}` : "Job Title & Department: -";
          const placeOfWorkText = block.placeOfWork ? `Place of Work: ${block.placeOfWork}` : "Place of Work: -";
          const subjectText = block.subject ? `Subject: ${block.subject}` : "Subject: -";
          const dateText = block.date ? `Date: ${block.date}` : "Date: -";
          
          doc.text(surnameText, 15, yPosition + 18);
          doc.text(firstNameText, 15, yPosition + 25);
          doc.text(jobTitleText, 15, yPosition + 32);
          doc.text(placeOfWorkText, 15, yPosition + 39);
          doc.text(subjectText, 100, yPosition + 18);
          doc.text(dateText, 100, yPosition + 25);
          
          // Signature on the right side
          if (block.signature) {
            const signatureMaxWidth = 50;
            const signatureMaxHeight = 35;
            const signatureX = pageWidth - 75;
            const signatureY = yPosition + 20;
            
            // Signature label
            doc.setFontSize(8);
            doc.setTextColor(139, 22, 52);
            doc.setFont("helvetica", "bold");
            doc.text("Signature:", signatureX, signatureY - 2);
            
            await addSignatureToPDF(block.signature, signatureX, signatureY, signatureMaxWidth, signatureMaxHeight);
          }
          
          yPosition += boxHeight + 8;
        }
      }
      
      // ---- Section 3: Briefing Conducted By ----
      if (yPosition + 60 > pageHeight - 30) {
        doc.addPage();
        yPosition = 20;
      }
      
      doc.setFillColor(139, 22, 52);
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.rect(10, yPosition, pageWidth - 20, 8, "F");
      doc.text("3", 14, yPosition + 5.5);
      doc.text("Briefing Conducted By", 22, yPosition + 5.5);
      yPosition += 8;
      
      autoTable(doc, {
        startY: yPosition,
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 130 },
        },
        head: [
          [
            { content: "Briefing Conducted by:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            { content: record.briefingConductedBY || "", styles: { halign: "center" } },
          ],
          [
            { content: "Date:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            { content: record.date || "", styles: { halign: "center" } },
          ],
        ],
        body: [],
        styles: {
          font: "helvetica",
          fontSize: 9,
          cellPadding: 3,
          overflow: "linebreak",
          valign: "middle",
        },
        theme: "grid",
        tableWidth: pageWidth - 20,
        margin: { left: 10 },
        headStyles: {
          fillColor: [245, 245, 245],
          textColor: [0, 0, 0],
          lineWidth: 0.2,
        },
        bodyStyles: {
          lineWidth: 0.2,
          textColor: [0, 0, 0],
        },
      });
      
      yPosition = doc.lastAutoTable.finalY + 2;
      
      // Add Main Signature within Section 3
      if (record.signature) {
        yPosition += 5;
        
        const boxWidth = pageWidth - 20;
        const boxHeight = 40;
        
        doc.setDrawColor(200, 200, 200);
        doc.setFillColor(248, 248, 248);
        doc.roundedRect(10, yPosition, boxWidth, boxHeight, 3, 3, "FD");
        
        doc.setTextColor(139, 22, 52);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("Main Signature", 15, yPosition + 8);
        
        const signatureMaxWidth = 50;
        const signatureMaxHeight = 22;
        const signatureX = pageWidth - 75;
        const signatureY = yPosition + 12;
        
        // Signature label
        doc.setFontSize(8);
        doc.setTextColor(139, 22, 52);
        doc.setFont("helvetica", "bold");
        doc.text("Signature:", signatureX, signatureY - 2);
        
        await addSignatureToPDF(record.signature, signatureX, signatureY, signatureMaxWidth, signatureMaxHeight);
        yPosition += boxHeight + 8;
      }
      
      // ---- Footer Section ----
      const footerY = pageHeight - 25;
      drawRoundedRect(0, footerY, pageWidth, 25, 0, [248, 249, 250], [200, 200, 200]);
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`,
        20,
        footerY + 8
      );
      doc.text("Page 1 of 1", pageWidth - 30, footerY + 15);
      
      // Decorative border removed
      
      // Build filename and either save or upload
      const safeName = String(record.locationOfInduction || 'AsbestosBriefing').replace(/[\\/:"*?<>|]+/g, '-');
      
      let fileName;
      if (shouldUploadToSharePoint && uploadConfig) {
        fileName = buildSharePointFileName([
          addedByName(record),
          uploadConfig.siteName,
          toDdMmYyyy(record.date),
        ]);
      } else {
        fileName = `AsbestosBriefing_${safeName}.pdf`;
      }
      
      if (shouldUploadToSharePoint && uploadConfig) {
        try {
          const key = `${record._id || ''}|${record.locationOfInduction || ''}`;
          setUploadingKey(key);
          const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
          const dataUri = doc.output('datauristring');
          
          // Prepare SharePoint upload parameters
          const uploadParams = {
            fileName,
            fileBase64: dataUri,
            phaseType: uploadConfig.phaseType,
            phaseNumber: uploadConfig.phaseNumber,
            network: uploadConfig.network,
            siteName: uploadConfig.siteName, uploadContext: "hseq-forms-tablets", uploadSubFolder: "Asbestos Briefing"
          };
          
          const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
            method: 'POST',
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
            body: JSON.stringify(uploadParams)
          });
          if (res.status !== 204 && !res.ok) {
            let msg = 'SharePoint upload failed';
            try {
              const out = await res.json();
              msg = out?.message || msg;
            } catch (_) {}
            throw new Error(msg);
          }
          toast.success("PDF uploaded to SharePoint successfully!");
          if (uploadConfig.onSuccess) {
            uploadConfig.onSuccess();
          }
        } catch (err) {
          toast.error(err?.message || "SharePoint upload failed");
          if (uploadConfig.onError) {
            uploadConfig.onError(err);
          }
        } finally {
          setUploadingKey(null);
        }
      } else {
        doc.save(fileName);
        toast.success("Professional Asbestos Briefing PDF generated successfully!");
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      toast.error('Failed to generate PDF');
    }
  };

  const formatDate = (d) => {
    if (!d) return "—";
    const x = new Date(d);
    return isNaN(x.getTime()) ? String(d) : x.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <>
      <div style={{
        background: '#ffffff',
        borderRadius: '20px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        padding: '24px',
        marginBottom: '24px'
      }}>
        {/* Header Section */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1a202c',
            margin: 0
          }}>Asbestos Briefing</h2>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: "#8b1634",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <i className="fa fa-search" style={{
                position: 'absolute',
                left: '12px',
                color: '#8b1634',
                fontSize: '14px'
              }}></i>
              <input 
                type="text" 
                placeholder="Type to Search" 
                value={search} 
                onChange={e=>{ setSearch(e.target.value); setPage(1); }}
                style={{
                  padding: '10px 12px 10px 36px',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  outline: 'none',
                  width: '250px',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#8b1634'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div style={{
          overflowX: 'auto',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr 1.2fr 0.8fr 1fr 1fr 1.2fr',
            background: '#f7fafc',
            padding: '12px 16px',
            borderBottom: '1px solid #e2e8f0',
            gap: '16px',
            alignItems: 'center'
          }}>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Location of Induction</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Office</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Briefing Conducted by</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Date</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Added By</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Updated Date</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Actions</div>
          </div>

          {/* Table Body */}
          <div>
            {paged.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#718096',
                fontSize: '16px'
              }}>No records found.</div>
            ) : paged.map((r, idx) => {
                // First check if firstName and lastName are saved in the record
                // If not, try to find user from appUsers array
                let userName = '-';
                if (r.addedByFirstName && r.addedByLastName) {
                  userName = `${r.addedByFirstName} ${r.addedByLastName}`;
                } else {
                const user = appUsers.find(u => u._id === r.userId);
                  userName = user ? `${user.firstName} ${user.lastName}` : (r.userId || '-');
                }
                
                // Parse group data if it's stored as JSON strings
                let surnameGroup = r.surnameGroup;
                let firstNameGroup = r.firstNameGroup;
                
                if (typeof surnameGroup === 'string' && surnameGroup.startsWith('[')) {
                  try {
                    surnameGroup = JSON.parse(surnameGroup);
                    firstNameGroup = JSON.parse(firstNameGroup);
                  } catch (e) {
                    surnameGroup = [surnameGroup];
                    firstNameGroup = [firstNameGroup];
                  }
                }

                const updatedDate = r.updatedAt || r.createdAt;
                
                return (
                  <div 
                    key={r._id || idx}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1.2fr 1fr 1.2fr 0.8fr 1fr 1fr 1.2fr',
                      padding: '16px',
                      borderBottom: '1px solid #f1f5f9',
                      gap: '16px',
                      alignItems: 'center',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.locationOfInduction || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.office || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.briefingConductedBY || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.date || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{userName || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{formatDate(updatedDate)}</div>
                    <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                      {canEditDelete && (
                        <button 
                          onClick={()=>handleEdit(r)}
                          title="Edit"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <i className="fa fa-edit" style={{color:'#22c55e', fontSize: '16px'}}></i>
                        </button>
                      )}
                      <button 
                        onClick={()=>generatePDF(r)}
                        title="Generate PDF"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '6px',
                          borderRadius: '6px',
                          transition: 'background-color 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <i className="fa fa-file-pdf-o" style={{color:'#dc2626', fontSize: '16px'}}></i>
                      </button>
                      {(() => { 
                        const k = `${r._id || ''}|${r.locationOfInduction || ''}`; 
                        const busy = uploadingKey === k; 
                        return (
                          <button 
                            onClick={()=>{
                              if (!busy) {
                                setSharePointUploadRecord(r);
                                setSharePointPhaseType('phase');
                                setSharePointPhaseNumber('15');
                                setSharePointNetwork('');
                                setSharePointPhaseType("non-phase"); setPendingDirectUpload(true);
                              }
                            }}
                            disabled={busy}
                            title={busy ? "Uploading..." : "Upload to SharePoint"}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: busy ? 'not-allowed' : 'pointer',
                              padding: '6px',
                              borderRadius: '6px',
                              transition: 'background-color 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: busy ? 0.6 : 1
                            }}
                            onMouseEnter={(e) => !busy && (e.currentTarget.style.backgroundColor = '#f0fdf4')}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{color:'#22c55e', fontSize: '16px'}}></i>
                          </button>
                        ); 
                      })()}
                      {canEditDelete && (
                        <button 
                          onClick={()=>handleDelete(r._id)}
                          title="Delete"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <i className="fa fa-trash" style={{color:'#e53e3e', fontSize: '16px'}}></i>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Pagination */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginTop: '18px',
          gap: '12px'
        }}>
          <button 
            disabled={page===1 || filtered.length === 0} 
            onClick={()=>setPage(page-1)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: (page===1 || filtered.length === 0) ? '#f7fafc' : '#ffffff',
              color: (page===1 || filtered.length === 0) ? '#a0aec0' : '#8b1634',
              cursor: (page===1 || filtered.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >Prev</button>
          <span style={{
            fontWeight: '700',
            color: '#8b1634',
            fontSize: '16px'
          }}>{page} / {totalPages}</span>
          <button 
            disabled={page===totalPages || filtered.length === 0} 
            onClick={()=>setPage(page+1)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: (page===totalPages || filtered.length === 0) ? '#f7fafc' : '#ffffff',
              color: (page===totalPages || filtered.length === 0) ? '#a0aec0' : '#8b1634',
              cursor: (page===totalPages || filtered.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >Next</button>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Add Asbestos Briefing</h5>
                <button type="button" className="btn-close" onClick={()=>setShowAddModal(false)}></button>
              </div>
              <div className="modal-body">
                {/* Section 1: Location and Office */}
                <div className="row mb-3">
                  <div className="col-12">
                    <h6 style={{color: '#8b1634', fontWeight: 'bold', marginBottom: '15px'}}>1. LOCATION AND OFFICE DETAILS</h6>
                    <div className="row mb-2">
                      <div className="col-6">
                        <label>Location of Induction</label>
                        <input type="text" className="form-control" value={newBriefing.locationOfInduction} onChange={e=>setNewBriefing({...newBriefing, locationOfInduction: e.target.value})} />
                      </div>
                      <div className="col-6">
                        <label>Office</label>
                        <input type="text" className="form-control" value={newBriefing.office} onChange={e=>setNewBriefing({...newBriefing, office: e.target.value})} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Group Details */}
                <div className="row mb-3">
                  <div className="col-12">
                    <h6 style={{color: '#8b1634', fontWeight: 'bold', marginBottom: '15px'}}>2. GROUP DETAILS</h6>
                    <div className="row mb-2">
                      <div className="col-12">
                        <p style={{color: '#000', marginBottom: '15px', fontStyle: 'italic'}}>We the undersigned confirm that we have been briefed and understand the document(s) as detailed above</p>
                      </div>
                    </div>
                    
                    {/* Dynamic Group Entries */}
                    {groupEntries.map((entry, index) => (
                      <div key={index} className="card mb-3" style={{border: '1px solid #e0e0e0', borderRadius: '8px'}}>
                        <div className="card-body">
                          <div className="mb-3">
                            <h6 style={{color: '#8b1634', margin: 0}}>Group Member {index + 1}</h6>
                          </div>
                          
                          <div className="row mb-2">
                            <div className="col-6">
                              <label>Surname</label>
                              <input 
                                type="text" 
                                className="form-control" 
                                value={entry.surname} 
                                onChange={e => updateGroupEntry(index, 'surname', e.target.value)} 
                              />
                            </div>
                            <div className="col-6">
                              <label>First Name</label>
                              <input 
                                type="text" 
                                className="form-control" 
                                value={entry.firstName} 
                                onChange={e => updateGroupEntry(index, 'firstName', e.target.value)} 
                              />
                            </div>
                          </div>
                          
                          <div className="row mb-2">
                            <div className="col-6">
                              <label>Job Title & Department</label>
                              <input 
                                type="text" 
                                className="form-control" 
                                value={entry.jobTitleDepartment} 
                                onChange={e => updateGroupEntry(index, 'jobTitleDepartment', e.target.value)} 
                              />
                            </div>
                            <div className="col-6">
                              <label>Place of Work</label>
                              <input 
                                type="text" 
                                className="form-control" 
                                value={entry.placeOfWork} 
                                onChange={e => updateGroupEntry(index, 'placeOfWork', e.target.value)} 
                              />
                            </div>
                          </div>
                          
                          <div className="row mb-2">
                            <div className="col-6">
                              <label>Subject</label>
                              <input 
                                type="text" 
                                className="form-control" 
                                value={entry.subject} 
                                onChange={e => updateGroupEntry(index, 'subject', e.target.value)} 
                              />
                            </div>
                            <div className="col-6">
                              <label>Date</label>
                              <input 
                                type="date" 
                                className="form-control" 
                                value={entry.date ? entry.date.split('/').reverse().join('-') : ''} 
                                onChange={e => updateGroupEntry(index, 'date', e.target.value.split('-').reverse().join('/'))} 
                              />
                            </div>
                          </div>
                          
                          <div className="col-md-6 mb-2">
                            <label className="form-label fw-semibold text-secondary">Signature</label>
                            <div style={{
                              width: '100%',
                              border: '2px solid #8b1634',
                              borderRadius: '18px',
                              background: '#e9cfd6',
                              padding: '0.5rem',
                              textAlign: 'left',
                              position: 'relative',
                              minHeight: '120px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center'
                            }}>
                              <SignatureCanvas
                                ref={ref => groupEntryRefs.current[index] = ref}
                                penColor="#8b1634"
                                backgroundColor="#e9cfd6"
                                clearOnResize={false}
                                velocityFilterWeight={0.7}
                                minWidth={1}
                                maxWidth={2.5}
                                dotSize={1.5}
                                throttle={16}
                                canvasProps={{
                                  width: 350,
                                  height: 100,
                                  style: {
                                    width: '100%',
                                    height: '100px',
                                    border: 'none',
                                    borderRadius: '18px',
                                    background: '#e9cfd6',
                                    display: 'block',
                                    touchAction: 'none'
                                  }
                                }}
                                onEnd={() => saveGroupSignature(index)}
                              />
                              <div style={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                marginTop: '4px'
                              }}>
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm rounded-pill px-3"
                                  style={{
                                    background: 'transparent',
                                    color: '#8b1634',
                                    borderColor: '#8b1634'
                                  }}
                                  onClick={() => clearGroupSignature(index)}>
                                  Clear
                                </button>
                              </div>
                            </div>
                          </div>
                          
                          {/* Remove Button at Bottom Right */}
                          <div className="d-flex justify-content-end mt-3">
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={() => removeGroupEntry(index)}
                              disabled={groupEntries.length === 1}
                              style={{
                                borderRadius: '50%', 
                                width: '30px', 
                                height: '30px', 
                                padding: 0,
                                background: groupEntries.length === 1 ? '#eaeaea' : '#8b1634',
                                color: groupEntries.length === 1 ? '#aaa' : '#fff',
                                border: 'none'
                              }}
                              title="Remove Group Member"
                            >
                              <i className="fa fa-minus"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                  </div>
                </div>

                {/* Section 3: Briefing Conducted by */}
                <div className="row mb-3">
                  <div className="col-12">
                    <h6 style={{color: '#8b1634', fontWeight: 'bold', marginBottom: '15px'}}>3. BRIEFING CONDUCTED BY</h6>
                    <div className="row mb-2">
                      <div className="col-6">
                        <label>Briefing Conducted by</label>
                        <input type="text" className="form-control" value={newBriefing.briefingConductedBY} onChange={e=>setNewBriefing({...newBriefing, briefingConductedBY: e.target.value})} />
                      </div>
                      <div className="col-6">
                        <label>Date</label>
                        <input type="date" className="form-control" value={newBriefing.date ? newBriefing.date.split('/').reverse().join('-') : ''} onChange={e=>setNewBriefing({...newBriefing, date: e.target.value.split('-').reverse().join('/')})} />
                      </div>
                    </div>
                    <div className="col-md-6 mb-2">
                      <label className="form-label fw-semibold text-secondary">Signature</label>
                      <div style={{
                        width: '100%',
                        border: '2px solid #8b1634',
                        borderRadius: '18px',
                        background: '#e9cfd6',
                        padding: '0.5rem',
                        textAlign: 'left',
                        position: 'relative',
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}>
                        <SignatureCanvas
                          ref={sigCanvasRef}
                          penColor="#8b1634"
                          backgroundColor="#e9cfd6"
                          clearOnResize={false}
                          velocityFilterWeight={0.7}
                          minWidth={1}
                          maxWidth={2.5}
                          dotSize={1.5}
                          throttle={16}
                          canvasProps={{
                            width: 350,
                            height: 100,
                            style: {
                              width: '100%',
                              height: '100px',
                              border: 'none',
                              borderRadius: '18px',
                              background: '#e9cfd6',
                              display: 'block',
                              touchAction: 'none'
                            }
                          }}
                          onEnd={saveSignature}
                        />
                        <div style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'flex-end',
                          marginTop: '4px'
                        }}>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm rounded-pill px-3"
                            style={{
                              background: 'transparent',
                              color: '#8b1634',
                              borderColor: '#8b1634'
                            }}
                            onClick={clearSignature}>
                            Clear
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowAddModal(false)}>Cancel</button>
                <button type="button" className="btn" style={{background:'#8b1634', color:'#fff', fontWeight:600}} onClick={handleAdd}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editBriefing && (
        <div className="modal fade show" style={adminEditModalOverlayStyle} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content" style={adminEditModalContentStyle}>
              <div className="modal-header" style={adminEditModalHeaderStyle}>
                <h5 className="modal-title" style={adminEditModalTitleStyle}>Edit Asbestos Briefing</h5>
                <button type="button" className="btn-close btn-close-white" onClick={()=>setShowEditModal(false)}></button>
              </div>
              <div className="modal-body" style={adminEditModalBodyStyle}>
                {/* Section 1: Location and Office */}
                <div className="row g-3 mb-3">
                  <div className="col-12">
                    <h6 style={{ color: ADMIN_THEME, fontWeight: 'bold', marginBottom: '15px' }}>1. LOCATION AND OFFICE DETAILS</h6>
                    <div className="row g-3 mb-2">
                      <div className="col-12 col-md-6">
                        <label style={adminEditLabelStyle}>Location of Induction</label>
                        <input type="text" className="form-control" style={adminEditInputStyle} value={editBriefing.locationOfInduction} onChange={e=>setEditBriefing({...editBriefing, locationOfInduction: e.target.value})} />
                      </div>
                      <div className="col-12 col-md-6">
                        <label style={adminEditLabelStyle}>Office</label>
                        <input type="text" className="form-control" style={adminEditInputStyle} value={editBriefing.office} onChange={e=>setEditBriefing({...editBriefing, office: e.target.value})} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Group Details */}
                <div className="row g-3 mb-3">
                  <div className="col-12">
                    <h6 style={{ color: ADMIN_THEME, fontWeight: 'bold', marginBottom: '15px' }}>2. GROUP DETAILS</h6>
                    <div className="row g-3 mb-2">
                      <div className="col-12">
                        <p style={{color: '#000', marginBottom: '15px', fontStyle: 'italic'}}>We the undersigned confirm that we have been briefed and understand the document(s) as detailed above</p>
                      </div>
                    </div>
                    
                    {/* Dynamic Group Entries */}
                    {editGroupEntries.map((entry, index) => (
                      <div key={index} className="card mb-3 mt-2" style={{ border: '1px solid #e8e8e8', borderRadius: 12, padding: 4 }}>
                        <div className="card-body">
                          <div className="mb-3">
                            <h6 style={{ color: ADMIN_THEME, margin: 0 }}>Group Member {index + 1}</h6>
                          </div>
                          
                          <div className="row g-3 mb-2">
                            <div className="col-12 col-md-6">
                              <label style={adminEditLabelStyle}>Surname</label>
                              <input type="text" className="form-control" style={adminEditInputStyle} value={entry.surname} 
                                onChange={e => updateEditGroupEntry(index, 'surname', e.target.value)} 
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <label style={adminEditLabelStyle}>First Name</label>
                              <input type="text" className="form-control" style={adminEditInputStyle} value={entry.firstName} 
                                onChange={e => updateEditGroupEntry(index, 'firstName', e.target.value)} 
                              />
                            </div>
                          </div>
                          
                          <div className="row g-3 mb-2">
                            <div className="col-12 col-md-6">
                              <label style={adminEditLabelStyle}>Job Title & Department</label>
                              <input type="text" className="form-control" style={adminEditInputStyle} value={entry.jobTitleDepartment} 
                                onChange={e => updateEditGroupEntry(index, 'jobTitleDepartment', e.target.value)} 
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <label style={adminEditLabelStyle}>Place of Work</label>
                              <input type="text" className="form-control" style={adminEditInputStyle} value={entry.placeOfWork} 
                                onChange={e => updateEditGroupEntry(index, 'placeOfWork', e.target.value)} 
                              />
                            </div>
                          </div>
                          
                          <div className="row g-3 mb-2">
                            <div className="col-12 col-md-6">
                              <label style={adminEditLabelStyle}>Subject</label>
                              <input type="text" className="form-control" style={adminEditInputStyle} value={entry.subject} 
                                onChange={e => updateEditGroupEntry(index, 'subject', e.target.value)} 
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <label style={adminEditLabelStyle}>Date</label>
                              <div className="site-register-datepicker-wrap">
                                <DatePicker
                                  {...siteRegisterDatePickerProps}
                                  selected={parseSiteRegisterDate(entry.date)}
                                  onChange={(d) => updateEditGroupEntry(index, 'date', formatSiteRegisterDate(d))}
                                />
                              </div>
                            </div>
                          </div>
                          
                          <div className="col-md-6 mb-2">
                            <label style={adminEditLabelStyle}>Signature</label>
                            {entry.signature ? (
                              // Show read-only image for existing signatures
                              <div style={adminEditSigBoxStyle()}>
                                <img 
                                  src={entry.signature.startsWith('data:image') 
                                    ? entry.signature 
                                    : `data:image/png;base64,${entry.signature}`}
                                  alt="Group Signature" 
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: '100px',
                                    objectFit: 'contain',
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    padding: '4px',
                                    backgroundColor: 'white',
                                    opacity: 1,
                                    filter: 'contrast(1.1) brightness(1)'
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </div>
                            ) : (
                              // Show editable signature pad for new entries
                              <div style={{
                                width: '100%',
                                border: '2px solid #8b1634',
                                borderRadius: '18px',
                                background: '#e9cfd6',
                                padding: '0.5rem',
                                textAlign: 'left',
                                position: 'relative',
                                minHeight: '120px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center'
                              }}>
                                <div style={{
                                  width: '100%',
                                  height: '100px',
                                  position: 'relative',
                                  display: 'block'
                                }}>
                                  <SignatureCanvas
                                    ref={ref => editGroupEntryRefs.current[index] = ref}
                                    penColor="#8b1634"
                                    backgroundColor="white"
                                    clearOnResize={false}
                                    velocityFilterWeight={0.7}
                                    minWidth={1}
                                    maxWidth={2.5}
                                    dotSize={1.5}
                                    throttle={16}
                                    canvasProps={{
                                      width: 400,
                                      height: 100,
                                      style: {
                                        width: '100%',
                                        height: '100px',
                                        border: 'none',
                                        borderRadius: '12px',
                                        background: 'white',
                                        display: 'block',
                                        touchAction: 'none',
                                        cursor: 'crosshair',
                                        position: 'relative'
                                      }
                                    }}
                                  />
                                </div>
                                <div style={{
                                  width: '100%',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginTop: '8px'
                                }}>
                                  <span style={{
                                    fontSize: '12px',
                                    color: '#8b1634',
                                    fontStyle: 'italic'
                                  }}>
                                    Draw your signature above
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm rounded-pill px-3"
                                    style={{
                                      background: 'transparent',
                                      color: '#8b1634',
                                      borderColor: '#8b1634'
                                    }}
                                    onClick={() => clearEditGroupSignature(index)}>
                                    Clear
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Remove Button at Bottom Right */}
                          <div className="d-flex justify-content-end mt-3">
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={() => removeEditGroupEntry(index)}
                              disabled={editGroupEntries.length === 1}
                              style={{
                                borderRadius: '50%', 
                                width: '30px', 
                                height: '30px', 
                                padding: 0,
                                background: editGroupEntries.length === 1 ? '#eaeaea' : '#8b1634',
                                color: editGroupEntries.length === 1 ? '#aaa' : '#fff',
                                border: 'none'
                              }}
                              title="Remove Group Member"
                            >
                              <i className="fa fa-minus"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                  </div>
                </div>

                {/* Section 3: Briefing Conducted by */}
                <div className="row g-3 mb-3">
                  <div className="col-12">
                    <h6 style={{ color: ADMIN_THEME, fontWeight: 'bold', marginBottom: '15px' }}>3. BRIEFING CONDUCTED BY</h6>
                    <div className="row g-3 mb-2">
                      <div className="col-12 col-md-6">
                        <label style={adminEditLabelStyle}>Briefing Conducted by</label>
                        <input type="text" className="form-control" style={adminEditInputStyle} value={editBriefing.briefingConductedBY} onChange={e=>setEditBriefing({...editBriefing, briefingConductedBY: e.target.value})} />
                      </div>
                      <div className="col-12 col-md-6">
                        <label style={adminEditLabelStyle}>Date</label>
                        <div className="site-register-datepicker-wrap">
                          <DatePicker
                            {...siteRegisterDatePickerProps}
                            selected={parseSiteRegisterDate(editBriefing.date)}
                            onChange={(d) => setEditBriefing({...editBriefing, date: formatSiteRegisterDate(d)})}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 mb-2">
                      <label style={adminEditLabelStyle}>Signature</label>
                      <div style={{
                        width: '100%',
                        border: '2px solid #8b1634',
                        borderRadius: '18px',
                        background: '#e9cfd6',
                        padding: '0.5rem',
                        textAlign: 'left',
                        position: 'relative',
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        <div style={{
                          width: '100%',
                          height: '100px',
                          position: 'relative',
                          display: 'block'
                        }}>
                          <SignatureCanvas
                            ref={editSigCanvasRef}
                            penColor="#8b1634"
                            backgroundColor="white"
                            clearOnResize={false}
                            velocityFilterWeight={0.7}
                            minWidth={1}
                            maxWidth={2.5}
                            dotSize={1.5}
                            throttle={16}
                            canvasProps={{
                              width: 400,
                              height: 100,
                              style: {
                                width: '100%',
                                height: '100px',
                                border: 'none',
                                borderRadius: '12px',
                                background: 'white',
                                display: 'block',
                                touchAction: 'none',
                                cursor: 'crosshair',
                                position: 'relative'
                              }
                            }}
                          />
                        </div>
                        <div style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginTop: '8px'
                        }}>
                          <span style={{
                            fontSize: '12px',
                            color: '#8b1634',
                            fontStyle: 'italic'
                          }}>
                            Draw your signature above
                          </span>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm rounded-pill px-3"
                            style={{
                              background: 'transparent',
                              color: '#8b1634',
                              borderColor: '#8b1634'
                            }}
                            onClick={clearEditSignature}>
                            Clear
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={adminEditModalFooterStyle}>
                <button type="button" className="btn" style={adminEditCancelBtnStyle} onClick={()=>setShowEditModal(false)}>Cancel</button>
                <button type="button" className="btn" style={adminEditSaveBtnStyle} onClick={handleEditSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SharePoint Upload Modal */}
      {showSharePointUploadModal && sharePointUploadRecord && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'600px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Upload to SharePoint</h5>
                <button type="button" className="btn-close" onClick={()=>{
                  setShowSharePointUploadModal(false);
                  setSharePointUploadRecord(null);
                  setSharePointPhaseType('phase');
                  setSharePointPhaseNumber('15');
                  setSharePointNetwork('');
                }}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-3 col-12">
                    <label className="form-label fw-semibold">Type <span style={{color:'red'}}>*</span></label>
                    <select 
                      className="form-select" 
                      value={sharePointPhaseType}
                      onChange={(e) => {
                        setSharePointPhaseType(e.target.value);
                        if (e.target.value === 'non-phase') {
                          setSharePointNetwork('');
                        }
                      }}
                    >
                      <option value="phase">Phase</option>
                      <option value="non-phase">Non Phase</option>
                    </select>
                  </div>
                  
                  {sharePointPhaseType === 'phase' && (
                    <>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Phase Number <span style={{color:'red'}}>*</span></label>
                        <select 
                          className="form-select" 
                          value={sharePointPhaseNumber}
                          onChange={(e) => {
                            setSharePointPhaseNumber(e.target.value);
                          }}
                        >
                          <option value="15">Phase 15 (2025)</option>
                          <option value="16">Phase 16 (2026)</option>
                        </select>
                      </div>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Area <span style={{color:'red'}}>*</span></label>
                        <select 
                          className="form-select" 
                          value={sharePointNetwork}
                          onChange={(e) => {
                            setSharePointNetwork(e.target.value);
                          }}
                        >
                          <option value="">Select Area</option>
                          <option value="EPN">EPN</option>
                          <option value="LPN">LPN</option>
                          <option value="SPN">SPN</option>
                        </select>
                      </div>
                    </>
                  )}
                  
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={()=>{
                    setShowSharePointUploadModal(false);
                    setSharePointUploadRecord(null);
                    setSharePointPhaseType('phase');
                    setSharePointPhaseNumber('15');
                    setSharePointNetwork('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn" 
                  style={{background:'#8b1634', color:'#fff', fontWeight:600}}
                  onClick={handleSharePointUpload}
                >
                  Upload to SharePoint
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload Asbestos Briefing File"
        importPath="asbestos-briefing/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setAsbestosBriefings((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Delete Asbestos Briefing</h5>
                <button type="button" className="btn-close" onClick={()=>setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this asbestos briefing?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Group Member Delete Confirmation Modal */}
      {showGroupMemberDeleteModal && groupMemberToDelete !== null && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'500px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Remove Group Member</h5>
                <button type="button" className="btn-close" onClick={()=>{
                  setShowGroupMemberDeleteModal(false);
                  setGroupMemberToDelete(null);
                }}></button>
              </div>
              <div className="modal-body">
                {(() => {
                  const entry = editGroupEntries[groupMemberToDelete];
                  const memberName = entry.firstName || entry.surname 
                    ? `${entry.firstName || ''} ${entry.surname || ''}`.trim() 
                    : `Group Member ${groupMemberToDelete + 1}`;
                  return (
                    <p>Are you sure you want to remove <strong>{memberName || 'this group member'}</strong>?</p>
                  );
                })()}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={()=>{
                    setShowGroupMemberDeleteModal(false);
                    setGroupMemberToDelete(null);
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={confirmRemoveEditGroupEntry}
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AsbestosBriefingPage;
